import React from "react";
import Resources from "../../../types/Resources";
import ClickableLabelCheckbox from "../../Common/ClickableLabelCheckbox";
import ElectricalDisconnectionChecklistModal from "./ElectricalDisconnectionModal";
import ElectricFenceChecklistModal from "./ElectricFenceChecklistModal";
import Take2ChecklistModal from "./Take2ChecklistModal";
import WorkOnRoadChecklistModal from "./WorkOnRoadChecklistModal";
import ConstructionPowerChecklistModal from "./ConstructionPowerChecklistModal";
import LabelCheckbox from "../../Common/LabelCheckbox";
import MeasuresFormState from "../../../types/forms/Permits/Conditions/MeasuresFormState";
import ElectricDisconnectionChecklistFormState from "../../../types/forms/Permits/Conditions/ElectricDisconnectionChecklistFormState";
import Take2ChecklistFormState from "../../../types/forms/Permits/Conditions/Take2ChecklistFormState";
import ElectricFenceChecklistFormState from "../../../types/forms/Permits/Conditions/ElectricFenceChecklistFormState";
import WorkOnRoadChecklistFormState from "../../../types/forms/Permits/Conditions/WorkOnRoadChecklistFormState";
import ConstructionPowerChecklistFormState from "../../../types/forms/Permits/Conditions/ConstructionPowerChecklistFormState";
import BaseUserInfo from "../../../types/BaseUserInfo";
import { SignatureManager } from "../../../hooks/useSignature";
import SignatureModal from "../../Signatures/SignatureModal";
import { MeasuresManager } from "../../../hooks/useMeasuesState";
import { PermitType } from "../../../common/enums";
import { CounterSignatureRequestInputManager } from "../../../hooks/useCounterSignatureRequestInput";
import Measures from "../../../types/permits/conditions/Measures";
import { ELECTRIC_DISCONNECTION_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/ElectricDisconnectionChecklist";
import { ELECTRIC_FENCE_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/ElectricFenceChecklist";
import { TAKE2_CHECKLIST_VERSION_2026_02 } from "../../../types/permits/conditions/Take2Checklist";

type MeasuresProps = {
    dbMeasures: Measures | undefined;
    resources: Resources;
    formState: MeasuresFormState;
    onChange: (fn: (prevValue: MeasuresFormState) => MeasuresFormState) => void;
    baseUserInfo?: BaseUserInfo;
    permitType: PermitType;
    canManageContent: boolean;

    showPowerOffExternalDirectorFlammableProductSignature?: boolean;
    showPowerOnExternalDirectorFlammableProductSignature?: boolean;

    powerOffDirectorFlammableProductSignatureManager?: SignatureManager;
    powerOffPortOfficerSignatureManager?: SignatureManager;
    powerOffExternalDirectorFlammableProductSignatureManager?: SignatureManager;
    powerOffResponsibleElectricianSignatureManager?: SignatureManager;
    powerOffWorkLeaderSignatureManager?: SignatureManager;
    powerOnDirectorFlammableProductSignatureManager?: SignatureManager;
    powerOnPortOfficerSignatureManager?: SignatureManager;
    powerOnExternalDirectorFlammableProductSignatureManager?: SignatureManager;
    powerOnResponsibleElectricianSignatureManager?: SignatureManager;
    powerOnWorkLeaderSignatureManager?: SignatureManager;

    electricFencePowerOffCounterSignatureRequestHandler?: CounterSignatureRequestInputManager;
    electricFencePowerOnCounterSignatureRequestHandler?: CounterSignatureRequestInputManager;

    uiState: MeasuresManager;
};

function Measures({
    dbMeasures,
    resources,
    formState,
    baseUserInfo,
    permitType,
    canManageContent,

    powerOffDirectorFlammableProductSignatureManager,
    powerOffExternalDirectorFlammableProductSignatureManager,
    powerOffResponsibleElectricianSignatureManager,
    powerOffWorkLeaderSignatureManager,
    powerOffPortOfficerSignatureManager,

    powerOnDirectorFlammableProductSignatureManager,
    powerOnExternalDirectorFlammableProductSignatureManager,
    powerOnResponsibleElectricianSignatureManager,
    powerOnWorkLeaderSignatureManager,
    powerOnPortOfficerSignatureManager,

    uiState,
    electricFencePowerOffCounterSignatureRequestHandler,
    electricFencePowerOnCounterSignatureRequestHandler,

    onChange
}: MeasuresProps) {
    const {
        showElectricalDisconnectionChecklistModal,
        showElectricFenceChecklistModal,
        showTake2ChecklistModal,
        showWorkOnRoadChecklistModal,
        showConstructionPowerChecklistModal,
        setShowElectricalDisconnectionChecklistModal,
        setShowElectricFenceChecklistModal,
        setShowTake2ChecklistModal,
        setShowWorkOnRoadChecklistModal,
        setShowConstructionPowerChecklistModal
    } = uiState;

    function measuresChangeHandler(key: keyof MeasuresFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: MeasuresFormState = {
                ...prevValue,
                [key]: { ...prevValue[key], value: value }
            };

            return updatedFormState;
        });
    }

    function electricalDisconnectionChecklistChangeHandler(
        checklistChangeHandler: (
            prevValue: ElectricDisconnectionChecklistFormState
        ) => ElectricDisconnectionChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: MeasuresFormState = {
                ...prevValue,
                electricDisconnectionChecklist: checklistChangeHandler(prevValue.electricDisconnectionChecklist)
            };

            return updatedFormState;
        });
    }

    function electricFenceChecklistChangeHandler(
        checklistChangeHandler: (prevValue: ElectricFenceChecklistFormState) => ElectricFenceChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: MeasuresFormState = {
                ...prevValue,
                electricFenceChecklist: checklistChangeHandler(prevValue.electricFenceChecklist)
            };

            return updatedFormState;
        });
    }

    function take2ChecklistChangeHandler(
        checklistChangeHandler: (prevValue: Take2ChecklistFormState) => Take2ChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: MeasuresFormState = {
                ...prevValue,
                take2Checklist: checklistChangeHandler(prevValue.take2Checklist)
            };

            return updatedFormState;
        });
    }

    function workOnRoadChecklistChangeHandler(
        checklistChangeHandler: (prevValue: WorkOnRoadChecklistFormState) => WorkOnRoadChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: MeasuresFormState = {
                ...prevValue,
                workOnRoadChecklist: checklistChangeHandler(prevValue.workOnRoadChecklist)
            };

            return updatedFormState;
        });
    }

    function constructionPowerChecklistChangeHandler(
        checklistChangeHandler: (prevValue: ConstructionPowerChecklistFormState) => ConstructionPowerChecklistFormState
    ) {
        onChange((prevValue) => {
            const updatedFormState: MeasuresFormState = {
                ...prevValue,
                constructionPowerChecklist: checklistChangeHandler(prevValue.constructionPowerChecklist)
            };

            return updatedFormState;
        });
    }

    return (
        <>
            <h1 className="h6">{resources.get("Measures")}</h1>
            <ul className="list-unstyled">
                {permitType === PermitType.EnergyHarbourPermit ? (
                    <>
                        <li className="m-2">
                            <LabelCheckbox
                                id="intervention-on-process-equipment"
                                checked={formState.interventionOnProcessEquipment.value}
                                label={resources.get("InterventionOnProcessEquipment")}
                                onChange={(checked) => measuresChangeHandler("interventionOnProcessEquipment", checked)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="m-2">
                            <LabelCheckbox
                                id="is-flange-opening-included-in-print"
                                checked={formState.isFlangeOpeningIncludedInPrint.value}
                                label={resources.get("FlangeOpening")}
                                onChange={(checked) => measuresChangeHandler("isFlangeOpeningIncludedInPrint", checked)}
                                readOnly={!canManageContent}
                            />
                        </li>
                        <li className="m-2">
                            <LabelCheckbox
                                id="process-equipment-cleaned-and-pressure-free"
                                checked={formState.processEquipmentCleanedAndPressureFree.value}
                                label={resources.get("ProcessEquipmentCleanedAndPressureFree")}
                                onChange={(checked) =>
                                    measuresChangeHandler("processEquipmentCleanedAndPressureFree", checked)
                                }
                                readOnly={!canManageContent}
                            />
                        </li>
                    </>
                ) : null}
                <li className="m-2">
                    <LabelCheckbox
                        id="gas-free-declaration"
                        checked={formState.gasFreeDeclaration.value}
                        label={resources.get("GasFreeDeclaration")}
                        onChange={(checked) => measuresChangeHandler("gasFreeDeclaration", checked)}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-2">
                    <div className="d-flex align-items-center">
                        <input
                            type="checkbox"
                            className={`form-check-input me-1 ${!canManageContent ? "read-only-check" : ""}`}
                            checked={formState.demarcationMetersChecked.value}
                            onChange={(e) =>
                                canManageContent
                                    ? measuresChangeHandler("demarcationMetersChecked", e.target.checked)
                                    : false
                            }
                        />
                        {resources.get("Demarcation")}
                        <input
                            className={`form-control ms-1 me-1 ${!canManageContent ? "read-only-input" : ""}`}
                            type="number"
                            style={{ width: "70px" }}
                            min="0"
                            value={formState.demarcationMeters.value ?? ""}
                            onChange={(e) => measuresChangeHandler("demarcationMeters", e.target.value)}
                            readOnly={!canManageContent}
                        />
                        {resources.get("Meters")}
                    </div>
                    <span className="text-danger">{formState.demarcationMeters.errorMessage}</span>
                </li>
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="electrical-disconnection"
                        checked={formState.electricalDisconnection.value}
                        label={resources.get("ElectricalDisconnection")}
                        onChange={(checked) => measuresChangeHandler("electricalDisconnection", checked)}
                        onLabelClick={() => setShowElectricalDisconnectionChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="electric-fence"
                        checked={formState.electricFenceOnOff.value}
                        label={resources.get("ElectricFenceOnOff")}
                        onChange={(checked) => measuresChangeHandler("electricFenceOnOff", checked)}
                        onLabelClick={() => setShowElectricFenceChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                {permitType === PermitType.EnergyHarbourPermit ? (
                    <>
                        <li className="m-2">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    className={`form-check-input me-1 ${!canManageContent ? "read-only-check" : ""}`}
                                    checked={formState.guardsPerTeamChecked.value}
                                    onChange={(e) =>
                                        canManageContent
                                            ? measuresChangeHandler("guardsPerTeamChecked", e.target.checked)
                                            : false
                                    }
                                />
                                <input
                                    className={`form-control ms-1 me-1 ${!canManageContent ? "read-only-input" : ""}`}
                                    type="text"
                                    style={{ width: "90px" }}
                                    value={formState.guardType.value ?? ""}
                                    onChange={(e) => measuresChangeHandler("guardType", e.target.value)}
                                    readOnly={!canManageContent}
                                />
                                {resources.get("Guard")}
                                <input
                                    className={`form-control ms-1 me-1 ${!canManageContent ? "read-only-input" : ""}`}
                                    type="number"
                                    style={{ width: "70px" }}
                                    min="0"
                                    value={formState.numberOfGuardsPerTeam.value ?? ""}
                                    onChange={(e) => measuresChangeHandler("numberOfGuardsPerTeam", e.target.value)}
                                    readOnly={!canManageContent}
                                />
                                {resources.get("PerTeam")}
                            </div>
                            <span className="text-danger d-block">{formState.guardType.errorMessage}</span>
                            <span className="text-danger">{formState.numberOfGuardsPerTeam.errorMessage}</span>
                        </li>
                        <li className="m-2">
                            <div className="d-flex align-items-center">
                                <input
                                    type="checkbox"
                                    className={`form-check-input me-1 ${!canManageContent ? "read-only-check" : ""}`}
                                    checked={formState.fireGuardsPerTeamChecked.value}
                                    onChange={(e) =>
                                        canManageContent
                                            ? measuresChangeHandler("fireGuardsPerTeamChecked", e.target.checked)
                                            : false
                                    }
                                    id="fire-guard-per-team-checked"
                                />
                                <label htmlFor="fire-guard-per-team-checked">{resources.get("FireGuard")}</label>
                                <input
                                    className={`form-control ms-1 me-1 ${!canManageContent ? "read-only-input" : ""}`}
                                    type="number"
                                    style={{ width: "70px" }}
                                    min="0"
                                    value={formState.numberOfFireGuardsPerTeam.value ?? ""}
                                    onChange={(e) => measuresChangeHandler("numberOfFireGuardsPerTeam", e.target.value)}
                                    id="number-of-fire-guards-per-team"
                                    aria-errormessage="number-of-fire-guards-per-team__error-message"
                                    readOnly={!canManageContent}
                                />
                                <label htmlFor="number-of-fire-guards-per-team">{resources.get("PerTeam")}</label>
                            </div>
                            <span id="number-of-fire-guards-per-team__error-message" className="text-danger">
                                {formState.numberOfFireGuardsPerTeam.errorMessage}
                            </span>
                        </li>
                    </>
                ) : null}
                <li className="m-2">
                    <LabelCheckbox
                        id="line-guard-will-be-informed"
                        checked={formState.lineGuardWillBeInformed.value}
                        label={resources.get("LineGuardWillBeInformed")}
                        onChange={(checked) => measuresChangeHandler("lineGuardWillBeInformed", checked)}
                        readOnly={!canManageContent}
                    />
                </li>
                <li className="m-2">
                    <ClickableLabelCheckbox
                        id="take-2"
                        checked={formState.take2.value}
                        label={resources.get("Take2")}
                        onChange={(checked) => measuresChangeHandler("take2", checked)}
                        onLabelClick={() => setShowTake2ChecklistModal(true)}
                        readOnly={!canManageContent}
                    />
                </li>
                {dbMeasures?.workOnRoadChecklist.checklistVersion !== 0 ? (
                    <li className="m-2">
                        <ClickableLabelCheckbox
                            id="work-on-road"
                            checked={formState.workOnRoad.value}
                            label={resources.get("WorkOnRoad")}
                            onChange={(checked) => measuresChangeHandler("workOnRoad", checked)}
                            onLabelClick={() => setShowWorkOnRoadChecklistModal(true)}
                            readOnly={!canManageContent}
                        />
                    </li>
                ) : null}
                {dbMeasures?.constructionPowerChecklist.checklistVersion !== 0 ? (
                    <li className="m-2">
                        <ClickableLabelCheckbox
                            id="construction-power"
                            checked={formState.constructionPower.value}
                            label={resources.get("ConstructionPower")}
                            onChange={(checked) => measuresChangeHandler("constructionPower", checked)}
                            onLabelClick={() => setShowConstructionPowerChecklistModal(true)}
                            readOnly={!canManageContent}
                        />
                    </li>
                ) : null}
                {permitType === PermitType.EnergyHarbourPermit ? (
                    <li>
                        <div className="d-flex">
                            <label htmlFor="measures__other-information">{resources.get("Other")}</label>
                            <input
                                type="text"
                                className={`form-control ms-2 ${!canManageContent ? "read-only-input" : ""}`}
                                value={formState.otherInformation.value}
                                onChange={(e) => measuresChangeHandler("otherInformation", e.target.value)}
                                readOnly={!canManageContent}
                            />
                        </div>
                    </li>
                ) : null}
            </ul>
            <ElectricalDisconnectionChecklistModal
                formState={formState.electricDisconnectionChecklist}
                onChange={electricalDisconnectionChecklistChangeHandler}
                onHide={() => setShowElectricalDisconnectionChecklistModal(false)}
                resources={resources}
                show={showElectricalDisconnectionChecklistModal}
                canManageContent={canManageContent}
                checklistVersion={
                    dbMeasures?.electricDisconnectionChecklist.checklistVersion ??
                    ELECTRIC_DISCONNECTION_CHECKLIST_VERSION_2026_02
                }
            />
            <ElectricFenceChecklistModal
                checklistVersion={
                    dbMeasures?.electricFenceChecklist.checklistVersion ?? ELECTRIC_FENCE_CHECKLIST_VERSION_2026_02
                }
                formState={formState.electricFenceChecklist}
                onChange={electricFenceChecklistChangeHandler}
                onHide={() => setShowElectricFenceChecklistModal(false)}
                resources={resources}
                show={showElectricFenceChecklistModal}
                baseUserInfo={baseUserInfo}
                powerOffDirectorFlammableProductSignatureManager={powerOffDirectorFlammableProductSignatureManager}
                powerOffExternalDirectorFlammableProductSignatureManager={
                    powerOffExternalDirectorFlammableProductSignatureManager
                }
                powerOffResponsibleElectricianSignatureManager={powerOffResponsibleElectricianSignatureManager}
                powerOffWorkLeaderSignatureManager={powerOffWorkLeaderSignatureManager}
                powerOnDirectorFlammableProductSignatureManager={powerOnDirectorFlammableProductSignatureManager}
                powerOnExternalDirectorFlammableProductSignatureManager={
                    powerOnExternalDirectorFlammableProductSignatureManager
                }
                powerOnResponsibleElectricianSignatureManager={powerOnResponsibleElectricianSignatureManager}
                powerOnWorkLeaderSignatureManager={powerOnWorkLeaderSignatureManager}
                powerOffCounterSignatureRequestHandler={electricFencePowerOffCounterSignatureRequestHandler}
                powerOnCounterSignatureRequestHandler={electricFencePowerOnCounterSignatureRequestHandler}
                powerOffPortOfficerSignatureManager={powerOffPortOfficerSignatureManager}
                powerOnPortOfficerSignatureManager={powerOnPortOfficerSignatureManager}
                canManageContent={canManageContent}
            />
            <Take2ChecklistModal
                checklistVersion={dbMeasures?.take2Checklist.checklistVersion ?? TAKE2_CHECKLIST_VERSION_2026_02}
                formState={formState.take2Checklist}
                onChange={take2ChecklistChangeHandler}
                onHide={() => setShowTake2ChecklistModal(false)}
                resources={resources}
                show={showTake2ChecklistModal}
                canManageContent={canManageContent}
            />
            <WorkOnRoadChecklistModal
                formState={formState.workOnRoadChecklist}
                onChange={workOnRoadChecklistChangeHandler}
                onHide={() => setShowWorkOnRoadChecklistModal(false)}
                resources={resources}
                show={showWorkOnRoadChecklistModal}
                canManageContent={canManageContent}
            />
            <ConstructionPowerChecklistModal
                formState={formState.constructionPowerChecklist}
                onChange={constructionPowerChecklistChangeHandler}
                onHide={() => setShowConstructionPowerChecklistModal(false)}
                resources={resources}
                show={showConstructionPowerChecklistModal}
                canManageContent={canManageContent}
            />
            {powerOffDirectorFlammableProductSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOffDirectorFlammableProductSignatureManager}
                    ref={powerOffDirectorFlammableProductSignatureManager.signaturePadRef}
                />
            ) : null}

            {powerOffExternalDirectorFlammableProductSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOffExternalDirectorFlammableProductSignatureManager}
                    ref={powerOffExternalDirectorFlammableProductSignatureManager.signaturePadRef}
                />
            ) : null}

            {powerOffResponsibleElectricianSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOffResponsibleElectricianSignatureManager}
                    ref={powerOffResponsibleElectricianSignatureManager.signaturePadRef}
                />
            ) : null}
            {powerOffWorkLeaderSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOffWorkLeaderSignatureManager}
                    ref={powerOffWorkLeaderSignatureManager.signaturePadRef}
                />
            ) : null}

            {powerOnDirectorFlammableProductSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOnDirectorFlammableProductSignatureManager}
                    ref={powerOnDirectorFlammableProductSignatureManager.signaturePadRef}
                />
            ) : null}

            {powerOnExternalDirectorFlammableProductSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOnExternalDirectorFlammableProductSignatureManager}
                    ref={powerOnExternalDirectorFlammableProductSignatureManager.signaturePadRef}
                />
            ) : null}

            {powerOnResponsibleElectricianSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOnResponsibleElectricianSignatureManager}
                    ref={powerOnResponsibleElectricianSignatureManager.signaturePadRef}
                />
            ) : null}

            {powerOnWorkLeaderSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOnWorkLeaderSignatureManager}
                    ref={powerOnWorkLeaderSignatureManager.signaturePadRef}
                />
            ) : null}

            {powerOffPortOfficerSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOffPortOfficerSignatureManager}
                    ref={powerOffPortOfficerSignatureManager.signaturePadRef}
                />
            ) : null}

            {powerOnPortOfficerSignatureManager ? (
                <SignatureModal
                    resources={resources}
                    SignatureManager={powerOnPortOfficerSignatureManager}
                    ref={powerOnPortOfficerSignatureManager.signaturePadRef}
                />
            ) : null}
        </>
    );
}

export default Measures;
